import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AnimatedButton from '../../utilities/AnimatedButton';
import {
  formatPrice,
  calculateDaysDiff,
} from '../../utilities/FormatFunctions';
import {
  PriceIcon,
  AreaIcon,
  BedroomsIcon,
  BathroomsIcon,
  CityIcon,
  LocationIcon,
} from '../../utilities/icons/RealEstatesIcons';
import { DoubleArrowRightIcon } from '../../utilities/icons/CommonIcons';

export default function Carousel({ realEstates }) {
  const [currentItem, setCurrentItem] = useState(0);
  const [showInfo, setShowInfo] = useState(false);
  const navigate = useNavigate();
  const intervalRef = useRef(null);
  const isPaused = useRef(false);

  // Fallback para vh en navegadores sin soporte de 100dvh
  useEffect(() => {
    const setVh = () => {
      document.documentElement.style.setProperty(
        '--vh',
        `${window.innerHeight * 0.01}px`
      );
    };
    setVh();
    window.addEventListener('resize', setVh);
    return () => window.removeEventListener('resize', setVh);
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => setShowInfo(true), 300);
    return () => clearTimeout(timeout);
  }, []);

  const startAutoSlide = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (!isPaused.current) {
        setShowInfo(false);
        setCurrentItem((prev) => (prev + 1) % realEstates.length);
      }
    }, 6000);
  }, [realEstates.length]);

  useEffect(() => {
    startAutoSlide();
    return () => clearInterval(intervalRef.current);
  }, [startAutoSlide]);

  const goToSlide = (index) => {
    setShowInfo(false);
    setCurrentItem(index);
    startAutoSlide();
  };

  const handlePrev = () => {
    const prevIndex =
      currentItem === 0 ? realEstates.length - 1 : currentItem - 1;
    goToSlide(prevIndex);
  };

  const handleNext = () => {
    const nextIndex =
      currentItem === realEstates.length - 1 ? 0 : currentItem + 1;
    goToSlide(nextIndex);
  };

  const handleDotClick = (index) => goToSlide(index);

  const handleNavigate = () => {
    navigate(`/ventas/detalle/${realEstates[currentItem].id}`);
  };

  const handleMouseEnter = () => (isPaused.current = true);
  const handleMouseLeave = () => (isPaused.current = false);

  const imageVariants = {
    enter: { opacity: 0, scale: 1.1, filter: 'blur(4px)' },
    center: {
      opacity: 1,
      scale: 1,
      filter: 'blur(0px)',
      transition: { duration: 1.2, ease: 'easeInOut' },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      filter: 'blur(4px)',
      transition: { duration: 1, ease: 'easeInOut' },
    },
  };

  const infoContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.05 } },
  };

  const infoVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <div
      className="relative w-full mt-32 overflow-hidden bg-"
      style={{
        height: 'calc(100dvh - 8rem)',
        minHeight: 'calc(var(--vh, 1vh) * 100 - 8rem)',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <AnimatePresence initial={false}>
        {realEstates.map(
          (item, index) =>
            index === currentItem && (
              <motion.div
                key={item.id}
                className="absolute inset-0 w-full h-full"
                variants={imageVariants}
                initial="enter"
                animate="center"
                exit="exit"
                onAnimationComplete={() => setShowInfo(true)}
              >
                <img
                  src={item.coverImageUrl}
                  alt={item.name}
                  className="w-full h-full object-cover pointer-events-none"
                />

                {/* Información y botón */}
                <AnimatePresence>
                  {showInfo && (
                    <motion.div
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      variants={infoContainer}
                      className="absolute inset-0 flex flex-col justify-between"
                    >
                      {/* Botón superior derecho */}
                      <motion.div
                        variants={infoVariants}
                        className="absolute top-4 right-4 z-40"
                      >
                        <AnimatedButton
                          label="Ver inmueble"
                          onClick={handleNavigate}
                          icon={DoubleArrowRightIcon}
                        />
                      </motion.div>

                      {/* Info inferior */}
                      <motion.div
                        variants={{
                          hidden: { opacity: 0, y: 50 },
                          visible: {
                            opacity: 1,
                            y: 0,
                            transition: { duration: 0.7, ease: 'easeOut' },
                          },
                        }}
                        className="absolute bottom-0 w-full h-[50%] bg-black/60 flex flex-col justify-start"
                      >
                        <div className="p-6 sm:px-6 md:px-8 h-full w-full flex flex-col justify-start text-white">
                          {/* Título y etiquetas */}
                          <div className="flex flex-col gap-2">
                            <motion.h1
                              variants={infoVariants}
                              className="text-xl sm:text-2xl font-heading md:text-3xl font-bold"
                            >
                              {item.name}
                            </motion.h1>
                            <motion.div
                              variants={infoVariants}
                              className="flex flex-wrap gap-2"
                            >
                              {calculateDaysDiff(item.createdAt) <= 15 && (
                                <span className="bg-amber-300 border font-heading border-gray-600 tracking-wide text-black font-medium shadow-custom-left-light text-[10px] md:text-xs px-2 py-0.5 md:px-3 md:py-1 rounded-full">
                                  ¡Nuevo!
                                </span>
                              )}
                              <span
                                className={`border font-heading border-gray-700 tracking-wide text-black font-medium shadow-custom-left-light text-[10px] md:text-xs px-2 py-0.5 md:px-3 md:py-1 rounded-full
                                  ${
                                    item.businessType !== 'Venta'
                                      ? 'bg-indigo-300'
                                      : 'bg-emerald-300'
                                  }`}
                              >
                                {item.businessType !== 'Venta'
                                  ? 'En arriendo'
                                  : 'En venta'}
                              </span>
                            </motion.div>
                          </div>
                          {/* Descripción */}
                          <motion.p
                            variants={infoVariants}
                            className="text-sm text-gray-300 tracking-tight sm:text-md md:text-lg my-2"
                          >
                            {item.description}
                          </motion.p>

                          {/* Características */}
                          <motion.div
                            variants={infoVariants}
                            className="flex flex-wrap font-bold text-gray-300 gap-x-4 sm:gap-x-6 md:gap-x-8 gap-y-2 mt-1 text-xs sm:text-sm"
                          >
                            <div className="flex items-center gap-1">
                              <PriceIcon className="text-sky-300 text-xs sm:text-sm md:text-base" />
                              <span className="flex items-baseline">
                                {formatPrice(item.price)}
                                {item.businessType === 'Arriendo' ? (
                                  <span className="text-[9px] sm:text-[10px] md:text-xs leading-none font-normal text-gray-200">
                                    /mes
                                  </span>
                                ) : null}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <AreaIcon className="text-sky-300 mr-1 text-xs sm:text-sm md:text-base" />
                              <span>{item.area} m²</span>
                            </div>
                            {item.realEstateTypeName.toLowerCase() ===
                              'lotes' && (
                              <>
                                <div className="flex items-center gap-1">
                                  <CityIcon className="text-sky-300 mr-1 text-xs sm:text-sm md:text-base" />
                                  <span>{item.city}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <LocationIcon className="text-sky-300 mr-1 text-xs sm:text-sm md:text-base" />
                                  <span>{item.sector}</span>
                                </div>
                              </>
                            )}
                            {item.realEstateTypeName.toLowerCase() !==
                              'lotes' && (
                              <>
                                <div className="flex items-center gap-1">
                                  <BedroomsIcon className="text-sky-300 mr-1 text-xs sm:text-sm md:text-base" />
                                  <span>
                                    {item.bedrooms}{' '}
                                    {item.bedrooms > 1
                                      ? 'habitaciones'
                                      : 'habitación'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <BathroomsIcon className="text-sky-300 mr-1 text-xs sm:text-sm md:text-base" />
                                  <span>
                                    {item.bathrooms}{' '}
                                    {item.bathrooms > 1 ? 'baños' : 'baño'}
                                  </span>
                                </div>
                              </>
                            )}
                          </motion.div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )
        )}
      </AnimatePresence>

      {/* Dots responsivos */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex">
        {realEstates.map((_, idx) => (
          <button
            key={idx}
            className={`w-2 h-2 sm:w-3 sm:h-3 mx-0.5 sm:mx-1 rounded-full border border-gray-400 transition-all duration-500 ease-in-out transform ${
              idx === currentItem
                ? 'bg-customYellow border-yellow-500 opacity-100 scale-125'
                : 'bg-white opacity-50 scale-100'
            }`}
            onClick={() => handleDotClick(idx)}
          />
        ))}
      </div>

      {/* Botones prev/next */}
      <button
        onClick={handlePrev}
        className="absolute top-1/2 left-2 -translate-y-1/2 z-30 flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-black/30 rounded-full hover:bg-black/50 hover:scale-110 transition-transform duration-200"
      >
        <svg
          className="w-4 h-4 sm:w-5 sm:h-5 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
      <button
        onClick={handleNext}
        className="absolute top-1/2 right-2 -translate-y-1/2 z-30 flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-black/30 rounded-full hover:bg-black/50 hover:scale-110 transition-transform duration-200"
      >
        <svg
          className="w-4 h-4 sm:w-5 sm:h-5 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}
